import { useRef, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { api, apiError } from "@/lib/api";

const TYPES = [
  { value: "general", label: "General enquiry" },
  { value: "trade", label: "Trade & sourcing" },
  { value: "supplier", label: "Supplier onboarding" },
  { value: "buyer", label: "Buyer / procurement" },
];

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

// Phase 1B: submits to the FastAPI intake API (DB record of truth + Resend notification).
export const EnquiryForm = ({ defaultType = "general", lockType = false, includeSupplierFields = false }) => {
  const mountedAt = useRef(Date.now());
  const [form, setForm] = useState({
    name: "", company: "", email: "", country: "", type: defaultType,
    licence: "", capability: "", message: "", website: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Please enter your name.";
    if (!form.company.trim()) err.company = "Please enter your company.";
    if (!emailValid(form.email)) err.email = "Please enter a valid email address.";
    if (form.message.trim().length < 5) err.message = "Please tell us how we can help.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/api/enquiries", {
        name: form.name, company: form.company, email: form.email, country: form.country,
        type: form.type, licence: form.licence, capability: form.capability, message: form.message,
        website: form.website, elapsed_ms: Date.now() - mountedAt.current,
      });
      toast.success(`Thank you — your enquiry has been received. Reference ${data.ref}.`);
      setForm({ name: "", company: "", email: "", country: "", type: defaultType,
        licence: "", capability: "", message: "", website: "" });
      setErrors({});
    } catch (err) {
      toast.error(apiError(err, "We couldn't send your enquiry. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const field =
    "w-full rounded-lg border border-ink-muted/25 bg-white px-4 py-3 text-sm text-ink-body placeholder:text-ink-muted/60 focus:outline-none focus:ring-2 focus:ring-azeka-teal focus:border-azeka-teal transition-shadow";
  const errCls = "mt-1.5 text-xs font-medium text-red-600";

  return (
    <form onSubmit={onSubmit} data-testid="enquiry-form" className="space-y-5" noValidate>
      <div aria-live="polite" className="sr-only" data-testid="form-errors">{Object.values(errors).join(" ")}</div>
      {/* honeypot — hidden from users, catches bots */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label>Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="enquiry-hp" /></label>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-heading">Full name</label>
          <input data-testid="enquiry-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
          {errors.name && <p className={errCls}>{errors.name}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-heading">Company</label>
          <input data-testid="enquiry-company" className={field} value={form.company} onChange={set("company")} placeholder="Company name" />
          {errors.company && <p className={errCls}>{errors.company}</p>}
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-heading">Email</label>
          <input data-testid="enquiry-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@company.com" />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-heading">Country</label>
          <input data-testid="enquiry-country" className={field} value={form.country} onChange={set("country")} placeholder="Country of operation" />
        </div>
      </div>

      {!lockType && (
        <div>
          <label className="mb-1.5 block text-sm font-medium text-ink-heading">Enquiry type</label>
          <select data-testid="enquiry-type" className={field} value={form.type} onChange={set("type")}>
            {TYPES.map((t) => (<option key={t.value} value={t.value}>{t.label}</option>))}
          </select>
        </div>
      )}

      {includeSupplierFields && (
        <div className="grid gap-5 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-heading">Licence / certification</label>
            <input data-testid="enquiry-licence" className={field} value={form.licence} onChange={set("licence")} placeholder="e.g. GMP, ISO, trade licence" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-heading">Product / capability</label>
            <input data-testid="enquiry-capability" className={field} value={form.capability} onChange={set("capability")} placeholder="What you supply" />
          </div>
        </div>
      )}

      <div>
        <label className="mb-1.5 block text-sm font-medium text-ink-heading">How can we help?</label>
        <textarea data-testid="enquiry-message" rows={5} className={field} value={form.message} onChange={set("message")} placeholder="Tell us about your requirement or capability" />
        {errors.message && <p className={errCls}>{errors.message}</p>}
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="enquiry-submit"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-azeka-navy px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-azeka-navy2 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-azeka-teal focus:ring-offset-2 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {submitting ? "Sending…" : "Send enquiry"} <Send className="h-4 w-4" />
      </button>
      <p className="text-xs text-ink-muted">
        Your enquiry will be routed to the appropriate Azeka team for review.
      </p>
    </form>
  );
};
